import React, { useEffect, useRef, useState } from "react";
import "./Howitworks.css";

const TikTokLogo = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M14.5 1C14.5 1 15 5 18.5 6.2V9.8C18.5 9.8 16.2 9.8 14.5 8.5V14C14.5 16.76 12.26 19 9.5 19C6.74 19 4.5 16.76 4.5 14C4.5 11.24 6.74 9 9.5 9V12.3C8.28 12.3 7.3 13.28 7.3 14.5C7.3 15.72 8.28 16.7 9.5 16.7C10.72 16.7 11.7 15.72 11.7 14.5V1H14.5Z" fill="white"/>
  </svg>
);

const IntegrateIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect x="3" y="3" width="9" height="9" rx="2" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6"/>
    <rect x="16" y="3" width="9" height="9" rx="2" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6"/>
    <rect x="3" y="16" width="9" height="9" rx="2" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6"/>
    <rect x="16" y="16" width="9" height="9" rx="2" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6"/>
    <path d="M12 7.5H16M7.5 12V16M20.5 12V16" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M16 20.5H20.5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

const WarehouseIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <path d="M4 13L14 5L24 13V24H4V13Z" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6" strokeLinejoin="round"/>
    <rect x="10" y="17" width="8" height="7" rx="1" fill="white" opacity="0.3" stroke="white" strokeWidth="1.4"/>
    <path d="M7 15H10M18 15H21" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 13H18" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const OrderIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect x="5" y="4" width="18" height="20" rx="2.5" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6"/>
    <path d="M9 10H19M9 14H16M9 18H13" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
    <circle cx="20" cy="20" r="5" fill="white"/>
    <path d="M17.5 20L19.2 21.7L22.5 18.5" stroke="#28479e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PackIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <path d="M14 3L24 8V20L14 25L4 20V8L14 3Z" fill="white" opacity="0.2" stroke="white" strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M14 3V25M4 8L14 13L24 8" stroke="white" strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M9 5.5L19 10.5" stroke="white" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
  </svg>
);

const TrackingIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="10" fill="white" opacity="0.15" stroke="white" strokeWidth="1.6"/>
    <circle cx="14" cy="14" r="4" fill="white" opacity="0.3" stroke="white" strokeWidth="1.4"/>
    <circle cx="14" cy="14" r="1.5" fill="white"/>
    <path d="M14 4V7M14 21V24M4 14H7M21 14H24" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M7.1 7.1L9.2 9.2M18.8 18.8L20.9 20.9M7.1 20.9L9.2 18.8M18.8 9.2L20.9 7.1" stroke="white" strokeWidth="1.3" strokeLinecap="round" opacity="0.5"/>
  </svg>
);

const steps = [
  {
    number: "01",
    icon: <IntegrateIcon />,
    accent: "blue",
    title: "Integrate your TikTok Shop",
    desc: "Connect your TikTok Shop for real-time inventory synchronisation and automated order processing workflows.",
  },
  {
    number: "02",
    icon: <WarehouseIcon />,
    accent: "orange",
    title: "Send inventory to our centres",
    desc: "Send inventory to our fulfilment centre UK for secure warehouse storage and efficient inventory management.",
  },
  {
    number: "03",
    icon: <OrderIcon />,
    accent: "blue",
    title: "Your customer places an order",
    desc: "TikTok Shop orders automatically sync and enter fulfilment workflows for fast, accurate order processing.",
  },
  {
    number: "04",
    icon: <PackIcon />,
    accent: "orange",
    title: "We pick, pack & ship the order",
    desc: "Our fulfilment team ensures accurate picking, secure packing, and reliable dispatch for every customer order.",
  },
  {
    number: "05",
    icon: <TrackingIcon />,
    accent: "blue",
    title: "Real-time tracking & returns",
    desc: "Provide customers with real-time order tracking, delivery updates, and streamlined returns management through a single platform.",
  },
];

const HowItWorks: React.FC = () => {
  const [visible, setVisible] = useState<boolean[]>(new Array(steps.length).fill(false));
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observers = refs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisible((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 120);
            obs.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, []);

  return (
    <section className="hiw-wrapper">
      {/* Background decoration */}
      <div className="hiw-bg-orb hiw-bg-orb--1" />
      <div className="hiw-bg-orb hiw-bg-orb--2" />
      <div className="hiw-bg-grid" />

      <div className="hiw-inner">
        {/* Header */}
        <div className="hiw-header">
          <div className="hiw-badge">
            <span className="hiw-badge-dot" />
            <span className="hiw-badge-tiktok"><TikTokLogo /></span>
            <span>TikTok Fulfilment</span>
          </div>
          <h2 className="hiw-title">
            How TikTok <span className="hiw-title-blue">fulfilment</span> works
          </h2>
          <p className="hiw-subtitle">
            Our advanced fulfilment platform simplifies TikTok Shop order management with real-time visibility and automation.
          </p>
        </div>

        {/* Steps */}
        <div className="hiw-steps">
          {/* Connector line */}
          <div className="hiw-connector">
            <div className="hiw-connector-track">
              <div className="hiw-connector-fill" />
            </div>
          </div>

          {steps.map((step, i) => (
            <div
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              className={`hiw-step hiw-step--${step.accent} ${visible[i] ? "hiw-step--visible" : ""} ${i % 2 === 0 ? "hiw-step--left" : "hiw-step--right"}`}
            >
              {/* Number bubble on the line */}
              <div className={`hiw-step-bubble hiw-step-bubble--${step.accent}`}>
                <span>{step.number}</span>
              </div>

              {/* Card */}
              <div className={`hiw-card hiw-card--${step.accent}`}>
                <div className="hiw-card-top">
                  <div className={`hiw-card-icon hiw-card-icon--${step.accent}`}>
                    {step.icon}
                  </div>
                  <span className={`hiw-card-num hiw-card-num--${step.accent}`}>{step.number}</span>
                </div>
                <h3 className="hiw-card-title">{step.title}</h3>
                <p className="hiw-card-desc">{step.desc}</p>
                <div className={`hiw-card-glow hiw-card-glow--${step.accent}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
